import React, { forwardRef, useEffect, useRef, useState, type ReactNode } from 'react';
import { cssMap, cx } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { Focusable } from '@atlaskit/primitives/compiled';
import mergeRefs from '../../../../utils/merge-refs.tsx';
import useLazyCallback from '../../../../utils/use-lazy-callback.tsx';
import useStateRef from '../../../../utils/use-state-ref.tsx';
import { keylineHeight } from './constants.tsx';
import { rafSchedule } from './utils.tsx';

const styles = cssMap({
	scrollContainerWrapper: {
		display: 'inherit',
		marginTop: token('space.0'),
		marginRight: token('space.0'),
		marginBottom: token('space.0'),
		marginLeft: token('space.0'),
		flex: 'inherit',
		flexDirection: 'inherit',
		overflowX: 'hidden',
		overflowY: 'auto',
	},
	topKeyline: {
		borderTop: `${token('border.width.selected')} solid ${token('color.border')}`,
	},
	bottomKeyline: {
		borderBottom: `${token('border.width.selected')} solid ${token('color.border')}`,
	},
});

// Based on atlassian-frontend/packages/design-system/modal-dialog/src/internal/components/scroll-container.tsx
type ScrollContainerProps = {
	/**
	 * Children of the body within modal dialog.
	 */
	children: ReactNode;
	/**
	 * A `testId` prop is provided for specified elements,
	 * which is a unique string that appears as a data attribute `data-testid` in the rendered code,
	 * serving as a hook for automated tests.
	 */
	testId?: string;
	/**
	 * Allow user to disable showing the keylines even when scrolling.
	 */
	disableAutoTopKeyline?: boolean;
	disableAutoBottomKeyline?: boolean;
};

/**
 * A container that shows top and bottom keylines when the
 * content overflows into the scrollable element.
 */
const ScrollContainer = forwardRef<HTMLDivElement, ScrollContainerProps>(
	(props: ScrollContainerProps, ref) => {
		const {
			testId,
			children,
			disableAutoTopKeyline = true,
			disableAutoBottomKeyline = true,
		} = props;
		const [hasSiblings, setSiblings] = useStateRef({
			previous: false,
			next: false,
		});

		const [showContentFocus, setContentFocus] = useState(false);
		const [showTopKeyline, setTopKeyline] = useState(false);
		const [showBottomKeyline, setBottomKeyline] = useState(false);

		const scrollableRef = useRef<HTMLDivElement>(null);

		const setLazySiblings = useLazyCallback(setSiblings);

		const setLazyContentFocus = useLazyCallback(
			rafSchedule(() => {
				const target = scrollableRef.current;
				target && setContentFocus(target.scrollHeight > target.clientHeight);
			}),
		);

		const setLazyKeylines = useLazyCallback(
			rafSchedule(() => {
				const target = scrollableRef.current;
				if (target) {
					const scrollableDistance = target.scrollHeight - target.clientHeight;

					if (hasSiblings.current.previous) {
						setTopKeyline(target.scrollTop > keylineHeight);
					}

					if (hasSiblings.current.next) {
						setBottomKeyline(target.scrollTop <= scrollableDistance - keylineHeight);
					}
				}
			}),
		);

		useEffect(() => {
			const target = scrollableRef.current;

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.addEventListener('resize', setLazyKeylines, false);
			target?.addEventListener('scroll', setLazyKeylines, false);

			setLazyContentFocus();
			setLazyKeylines();
			setLazySiblings({
				previous: Boolean(target?.previousElementSibling),
				next: Boolean(target?.nextElementSibling),
			});

			return () => {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.removeEventListener('resize', setLazyKeylines, false);
				target?.removeEventListener('scroll', setLazyKeylines, false);
			};
		}, [setLazyContentFocus, setLazyKeylines, setLazySiblings]);
		return (
			<Focusable
				as="div"
				isInset
				// eslint-disable-next-line @atlassian/a11y/no-noninteractive-tabindex
				tabIndex={showContentFocus ? 0 : undefined}
				testId={testId !== null && testId !== undefined ? `${testId}--scrollable` : undefined}
				ref={mergeRefs([ref, scrollableRef])}
				xcss={cx(
					styles.scrollContainerWrapper,
					!disableAutoTopKeyline && showTopKeyline && styles.topKeyline,
					!disableAutoBottomKeyline && showBottomKeyline && styles.bottomKeyline,
				)}
			>
				{children}
			</Focusable>
		);
	},
);

ScrollContainer.displayName = 'ScrollContainer';

export default ScrollContainer;
